// ============================================================================
//  depenses.api.ts — Couche API Module Dépenses (notes de frais)
//  Étape 5b-1 : liste + détail (consultation). Endpoints backend étape 2.
// ============================================================================

import api from '@/lib/axios'

// ─── Types (miroir backend) ───────────────────────────────────────────────────

export type ExpenseType    = 'AVANCE' | 'REMBOURSEMENT' | 'FACTURE_FOURNISSEUR'
export type ExpensePaymentMode = 'CASH' | 'BANK'
export type ExpenseStatus  = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'PAID' | 'JUSTIFIED'

export interface ExpenseAttachment {
  id:           string
  fileName:     string
  fileUrl:      string
  fileSize:     number
  mimeType:     string
  // Hérité : jamais vrai sur la fiche (la vérité légale est portée par l'écriture).
  isWormSealed: boolean
  createdAt:    string
  // Champs dérivés (GET /depenses/:id) — optionnels pour tolérer une réponse ancienne.
  /** Pièce scellée dans l'archive légale (WORM). */
  sealed?:               boolean
  /** Rattachée à l'écriture comptable : scellée à la validation de l'écriture. */
  attachedToEntry?:      boolean
  /** Ajoutée après le paiement (paidAt). */
  receivedAfterPayment?: boolean
}

/** Réponse de l'upload : la pièce créée + son rattachement à l'écriture comptable. */
export interface AttachJustificatifResult extends ExpenseAttachment {
  attachedToEntry: boolean
}

/** inline : affichage dans le navigateur (aperçu) ; attachment : téléchargement. */
export type FileDisposition = 'inline' | 'attachment'

/** Réponse de GET /depenses/:id/attachments/:attId/url (URL présignée, 10 min). */
export interface JustificatifUrl {
  url: string; fileName: string; mimeType: string; expiresIn: number
  /** Disposition réellement accordée : 'attachment' forcé si le type n'est pas affichable en ligne. */
  disposition: FileDisposition
}

/** Catégorie métier de dépense (miroir backend) — mappée à un compte classe 6. */
export interface ExpenseCategory {
  id:              string
  entityId:        string
  label:           string
  chargeAccountId: string
  isActive:        boolean
  createdAt:       string
  updatedAt:       string
}

export interface CreateExpenseCategoryInput {
  label:           string
  chargeAccountId: string
}

export interface UpdateExpenseCategoryInput {
  label?:           string
  chargeAccountId?: string
  isActive?:        boolean
}

/** Demandeur (fiche employé) — inclus en liste et en détail. */
export interface ExpenseRequester {
  id:        string
  matricule: string
  firstName: string
  lastName:  string
}

/** Acteur du workflow (compte utilisateur) — résolu par findOne. */
export interface ExpenseActor {
  id:        string
  firstName: string
  lastName:  string
}

export interface ExpenseRequest {
  id:              string
  entityId:        string
  reference:       string
  type:            ExpenseType
  employeeId:      string | null
  employee?:       ExpenseRequester | null
  description:     string
  amount:          number
  currency:        string
  paymentMode:     ExpensePaymentMode
  chargeAccountId: string | null
  categoryId:      string | null
  // Catégorie liée (incluse par findOne) — présente même si désactivée.
  category?:       { id: string; label: string; isActive: boolean } | null
  status:          ExpenseStatus
  createdById:     string
  validatedById:   string | null
  paidById:        string | null
  // Acteurs résolus (détail uniquement) — null si le compte est introuvable.
  createdBy?:      ExpenseActor | null
  validatedBy?:    ExpenseActor | null
  paidBy?:         ExpenseActor | null
  submittedAt:     string | null
  validatedAt:     string | null
  paidAt:          string | null
  journalEntryId:  string | null
  createdAt:       string
  updatedAt:       string
  justificatifs?:  ExpenseAttachment[]
}

export interface ListExpensesParams {
  status?: ExpenseStatus
  type?:   ExpenseType
  search?: string
  page?:   number
  limit?:  number
}

export interface PaginatedExpenses {
  data: ExpenseRequest[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

export interface CreateExpenseInput {
  // Plus saisi dans l'interface (défaut backend : REMBOURSEMENT)
  type?:       ExpenseType
  description: string
  amount:      number
  paymentMode: ExpensePaymentMode
  currency?:   string
  employeeId?: string
  // Obligatoire : porte le compte de charge de l'écriture comptable
  categoryId:  string
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const depensesApi = {
  // Liste (scopée côté backend selon les permissions)
  list: (params?: ListExpensesParams): Promise<PaginatedExpenses> =>
    api.get('/depenses', { params }).then(r => r.data.data ?? r.data),

  // Détail (inclut les justificatifs)
  getOne: (id: string): Promise<ExpenseRequest> =>
    api.get(`/depenses/${id}`).then(r => r.data.data ?? r.data),

  // Création (porte employé DRAFT, ou comptable SUBMITTED pour FACTURE_FOURNISSEUR)
  create: (data: CreateExpenseInput): Promise<ExpenseRequest> =>
    api.post('/depenses', data).then(r => r.data.data ?? r.data),

  // Transitions de workflow (le backend valide état + permissions + pas juge et partie)
  submit: (id: string): Promise<ExpenseRequest> =>
    api.post(`/depenses/${id}/submit`).then(r => r.data.data ?? r.data),

  validate: (id: string): Promise<ExpenseRequest> =>
    api.post(`/depenses/${id}/validate`).then(r => r.data.data ?? r.data),

  reject: (id: string, reason: string): Promise<ExpenseRequest> =>
    api.post(`/depenses/${id}/reject`, { reason }).then(r => r.data.data ?? r.data),

  // Paiement — le comptable renseigne le compte de charge classe 6
  pay: (id: string, chargeAccountId?: string): Promise<ExpenseRequest> =>
    api.post(`/depenses/${id}/pay`, chargeAccountId ? { chargeAccountId } : {}).then(r => r.data.data ?? r.data),

  // Régularisation d'une avance (PAID → JUSTIFIED). Transition seule.
  justify: (id: string): Promise<ExpenseRequest> =>
    api.post(`/depenses/${id}/justify`).then(r => r.data.data ?? r.data),

  // [B32-b] Justificatifs — upload (multipart) / suppression.
  // Upload accepté dans tout statut sauf REJECTED (après paiement : créateur ou depenses:pay).
  attachJustificatif: (id: string, file: File): Promise<AttachJustificatifResult> => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/depenses/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.data ?? r.data)
  },

  deleteJustificatif: (id: string, attId: string): Promise<{ deleted: boolean; id: string }> =>
    api.delete(`/depenses/${id}/attachments/${attId}`).then(r => r.data.data ?? r.data),

  // URL présignée (10 min) : 'inline' pour l'aperçu, 'attachment' (défaut) pour le téléchargement
  getJustificatifUrl: (id: string, attId: string, disposition: FileDisposition = 'attachment'): Promise<JustificatifUrl> =>
    api.get(`/depenses/${id}/attachments/${attId}/url`, { params: { disposition } }).then(r => r.data.data ?? r.data),

  // ─── Catégories de dépense ───────────────────────────────────────────────────
  // Lecture ouverte (depenses:read) ; création/màj/désactivation réservées à
  // depenses:manage_categories (le backend reste la barrière).

  // Liste des catégories. includeInactive (réservé manage_categories côté backend)
  // ajoute les catégories désactivées ; par défaut, seules les actives.
  listCategories: (includeInactive?: boolean): Promise<ExpenseCategory[]> =>
    api.get('/depenses/categories', { params: includeInactive ? { includeInactive: true } : undefined })
      .then(r => r.data.data ?? r.data),

  createCategory: (data: CreateExpenseCategoryInput): Promise<ExpenseCategory> =>
    api.post('/depenses/categories', data).then(r => r.data.data ?? r.data),

  updateCategory: (id: string, data: UpdateExpenseCategoryInput): Promise<ExpenseCategory> =>
    api.patch(`/depenses/categories/${id}`, data).then(r => r.data.data ?? r.data),

  // Soft delete (isActive=false) — jamais de suppression physique
  deleteCategory: (id: string): Promise<ExpenseCategory> =>
    api.delete(`/depenses/categories/${id}`).then(r => r.data.data ?? r.data),
}
